import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../AuthProvider";
import { usePermissions } from "../rbac/PermissionsProvider";
import { PERMISSIONS } from "../rbac/permissions";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Autocomplete,
  Button,
  Typography,
  Pagination,
  Alert,
  Chip,
  Skeleton,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import InventoryIcon from "@mui/icons-material/Inventory";
import CloseIcon from "@mui/icons-material/Close";

import { catalogTokens, parseApiError, formatMonthYear, SCM_ADMIN_EMAILS, groupCatalogsByVendor } from "./VendorCatalogTheme";
import VendorCatalogCard from "./VendorCatalogCard";
import VendorCatalogTimeline from "./VendorCatalogTimeline";
import VendorCatalogVendorDialog from "./VendorCatalogVendorDialog";
import KPICards from "../shared/kpi/KPICards"
import { computeScmUserUploadCards } from "./VendorCatalogKPI/VendorCatalogKpiDefs";
import useCategoryTaxonomy from "./useCategoryTaxonomy";

const EMPTY_FILTERS = {
  vendorId: "", categoryNames: [], brandNames: [], subCategoryNames: [], occasionIds: [], celebrationIds: [], minPrice: "", maxPrice: "", uploadedBy: "",
};

const MULTI_FILTER_KEYS = ["categoryNames", "brandNames", "subCategoryNames", "occasionIds", "celebrationIds"];

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: `${catalogTokens.radius.md}px`, bgcolor: catalogTokens.color.surface },
};

// Shared menu sizing/position for every checkbox multi-select dropdown, so they're visually
// consistent (previously duplicated inline on each Select).
const multiSelectMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      mt: 0.5,
      borderRadius: 2,
    },
  },
};

// Keeps the collapsed Select's summary text short instead of dumping every selected value inline.
const renderMultiValue = (selected, allLabel) => {
  if (!selected.length) return allLabel;
  if (selected.length <= 2) return selected.join(", ");
  return `${selected.slice(0, 2).join(", ")} +${selected.length - 2} more`;
};

const VendorCatalogSearch = () => {
  const { instance } = useMsal();
  const { account } = useAuth();
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.VENDOR_CATALOG_MANAGE);

  const [vendors, setVendors] = useState([]);
  const { categories, brands, subcategories: subCategoryNames, ensureSubcategoriesFor, collections } =
    useCategoryTaxonomy({ subcategoryFallback: "union" });
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vendorDialogTarget, setVendorDialogTarget] = useState(null); // { id, name } | null
  const [selectedMonth, setSelectedMonth] = useState(null); // { year, month } | null
  const [uploaderCounts, setUploaderCounts] = useState({});

  const authedFetch = useCallback(
    async (url, method = "GET") => {
      const token = await getApiToken(instance, account);
      const res = await fetch(url, { method, headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await parseApiError(res));
      const text = await res.text();
      return text ? JSON.parse(text) : {};
    },
    [instance, account]
  );

  useEffect(() => {
    if (!account) return;
    authedFetch(`${urlconfig.backend_api_base_url}/api/v2/vendors`)
      .then((data) => setVendors(data || []))
      .catch(() => setVendors([]));
  }, [account, authedFetch]);

  // Re-scope the Sub-Category list to the selected categories (falls back to the full
  // cross-category union when none is selected, per subcategoryFallback: "union" above).
  useEffect(() => {
    ensureSubcategoriesFor(filters.categoryNames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureSubcategoriesFor, filters.categoryNames.join(",")]);

      useEffect(() => {
        if (!account) return;
        let cancelled = false;
        const params = new URLSearchParams({
          ...(filters.vendorId && { vendorId: filters.vendorId }),
          ...(filters.categoryNames.length && { categoryName: filters.categoryNames.join(",") }),
          ...(filters.brandNames.length && { brandName: filters.brandNames.join(",") }),
          ...(filters.subCategoryNames.length && { subCategoryName: filters.subCategoryNames.join(",") }),
          ...(filters.occasionIds.length && { occasionId: filters.occasionIds.join(",") }),
          ...(filters.celebrationIds.length && { celebrationId: filters.celebrationIds.join(",") }),
          ...(filters.minPrice && { minPrice: filters.minPrice }),
          ...(filters.maxPrice && { maxPrice: filters.maxPrice }),
          ...(selectedMonth && { year: selectedMonth.year, month: selectedMonth.month }),
        });
        authedFetch(`${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/stats/by-uploader?${params.toString()}`)
          .then((data) => {
            if (cancelled) return;
            const counts = {};
            (data || []).forEach((row) => { counts[row.uploadedBy] = row.catalogCount; });
            setUploaderCounts(counts);
          })
          .catch(() => { if (!cancelled) setUploaderCounts({}); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        account, authedFetch, selectedMonth,
        filters.vendorId, filters.categoryNames.join(","), filters.brandNames.join(","),
        filters.subCategoryNames.join(","), filters.occasionIds.join(","), filters.celebrationIds.join(","),
      ]);

  const performSearch = useCallback(
    async (pageNo = 1, { silent = false, filtersOverride = null } = {}) => {
      if (!account) return;
      if (!silent) setLoading(true);
      setError(null);
      const f = filtersOverride || filters;
      try {
        const params = new URLSearchParams({
          page: pageNo - 1,
          size: 20,
          ...(f.vendorId && { vendorId: f.vendorId }),
          ...(f.categoryNames.length && { categoryName: f.categoryNames.join(",") }),
          ...(f.brandNames.length && { brandName: f.brandNames.join(",") }),
          ...(f.subCategoryNames.length && { subCategoryName: f.subCategoryNames.join(",") }),
          ...(f.occasionIds.length && { occasionId: f.occasionIds.join(",") }),
          ...(f.celebrationIds.length && { celebrationId: f.celebrationIds.join(",") }),
          ...(f.minPrice && { minPrice: f.minPrice }),
          ...(f.maxPrice && { maxPrice: f.maxPrice }),
          ...(f.uploadedBy && { uploadedBy: f.uploadedBy }),
          ...(selectedMonth && { year: selectedMonth.year, month: selectedMonth.month }),
        });
        const data = await authedFetch(`${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs?${params.toString()}`);
        setResults(data.content || []);
        setTotalPages(data.totalPages || 1);
        setTotalResults(data.totalElements ?? (data.content || []).length);
        setPage(pageNo);
      } catch (err) {
        setError(err.message || "Search failed");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [account, authedFetch, filters, selectedMonth]
  );

   const { cards: scmUserCards, values: scmUserValues } = useMemo(
        () => computeScmUserUploadCards(uploaderCounts),
        [uploaderCounts]
      );


  // Dropdown filters (select-from-a-list, not free text) re-run the search immediately, same as
  // the Timeline's counts already do — otherwise the results grid is left showing a stale
  // snapshot from before the filter changed, while the sidebar/chips have already moved on.
  // Min/Max Price stay on the explicit Search button, since those are typed character-by-character
  // and searching on every keystroke would be wasteful.
  useEffect(() => {
    if (account) performSearch(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    account, selectedMonth,
    filters.vendorId, filters.categoryNames.join(","), filters.brandNames.join(","), filters.subCategoryNames.join(","), filters.occasionIds.join(","), filters.celebrationIds.join(","), filters.uploadedBy,
  ]);

  // One card per vendor, not per catalog — see groupCatalogsByVendor. Only regroups when the
  // current page's results actually change.
  const vendorGroups = useMemo(() => groupCatalogsByVendor(results), [results]);

  // Generic handler for all checkbox multi-selects. `resetSubCategories` is set on the Category
  // field so a stale sub-category selection from a previous category set doesn't linger.
  const handleMultiSelectChange = (field, resetSubCategories = false) => (event) => {
    const { value } = event.target;
    // MUI can hand back a comma-joined string in some edge cases (e.g. autofill); normalize it.
    const next = typeof value === "string" ? value.split(",") : value;
    setFilters((prev) => ({
      ...prev,
      [field]: next,
      ...(resetSubCategories && { subCategoryNames: [] }),
    }));
  };

  const collectionNameById = (id) => collections.find((c) => String(c.id) === String(id))?.name || id;
  const selectedVendor = vendors.find((v) => String(v.id) === String(filters.vendorId)) || null;

  // Multi-value filters contribute one chip per selected value so each can be removed individually.
  const activeChips = [
    filters.vendorId && { key: "vendorId", label: selectedVendor?.legalVendorName || filters.vendorId },
    ...filters.categoryNames.map((v) => ({ key: "categoryNames", value: v, label: v })),
    ...filters.brandNames.map((v) => ({ key: "brandNames", value: v, label: v })),
    ...filters.subCategoryNames.map((v) => ({ key: "subCategoryNames", value: v, label: v })),
    ...filters.occasionIds.map((v) => ({ key: "occasionIds", value: v, label: collectionNameById(v) })),
    ...filters.celebrationIds.map((v) => ({ key: "celebrationIds", value: v, label: collectionNameById(v) })),
    filters.minPrice && { key: "minPrice", label: `Min ₹${filters.minPrice}` },
    filters.maxPrice && { key: "maxPrice", label: `Max ₹${filters.maxPrice}` },
    filters.uploadedBy && { key: "uploadedBy", label: `SCM: ${filters.uploadedBy.split("@")[0]}` },
    selectedMonth && { key: "month", label: formatMonthYear(selectedMonth.year, selectedMonth.month) },
  ].filter(Boolean);

  const clearFilter = (key, value) => {
    if (key === "month") {
      setSelectedMonth(null);
      return;
    }
    if (MULTI_FILTER_KEYS.includes(key)) {
      setFilters((prev) => ({ ...prev, [key]: prev[key].filter((v) => v !== value) }));
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: "" }));
  };

  return (
    <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
      <VendorCatalogTimeline
        vendorId={filters.vendorId}
        categoryName={filters.categoryNames.join(",")}
        brandName={filters.brandNames.join(",")}
        subCategoryName={filters.subCategoryNames.join(",")}
        occasionId={filters.occasionIds.join(",")}
        celebrationId={filters.celebrationIds.join(",")}
        minPrice={filters.minPrice}
        maxPrice={filters.maxPrice}
        uploadedBy={filters.uploadedBy}
        selected={selectedMonth}
        onSelect={setSelectedMonth}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {scmUserCards.length > 0 && (
          <KPICards
            cards={scmUserCards}
            values={scmUserValues}
            columns={{ xs: 6, sm: 4, md: 2.4 }}
            onCardClick={(card) => setFilters((prev) => ({ ...prev, uploadedBy: card.key }))}
          />
        )}

        <Box
          sx={{
            p: 2,
            mb: activeChips.length ? 1.5 : 3,
            borderRadius: `${catalogTokens.radius.lg}px`,
            bgcolor: catalogTokens.color.surfaceMuted,
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                size="small"
                options={vendors}
                getOptionLabel={(option) => option.legalVendorName || ""}
                isOptionEqualToValue={(o, v) => o?.id != null && v?.id != null && String(o.id) === String(v.id)}
                value={selectedVendor}
                onChange={(_, newVal) => setFilters({ ...filters, vendorId: newVal?.id || "" })}
                renderInput={(params) => <TextField {...params} label="Vendor" sx={fieldSx} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel shrink>Category</InputLabel>
                <Select
                  multiple
                  value={filters.categoryNames}
                  onChange={handleMultiSelectChange("categoryNames", true)}
                  input={<OutlinedInput notched label="Category" />}
                  renderValue={(selected) => renderMultiValue(selected, "All Categories")}
                  displayEmpty
                  MenuProps={multiSelectMenuProps}
                >
                  {categories.map((c) => {
                    const isChecked = filters.categoryNames.indexOf(c.name) > -1;
                    return (
                      <MenuItem key={c.id} value={c.name}>
                        <Checkbox checked={isChecked} size="small" sx={{ mr: 1, p: 0.5 }} />
                        <ListItemText
                          primary={c.name}
                          primaryTypographyProps={{ fontSize: 14, fontWeight: isChecked ? 600 : 400, noWrap: true }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel shrink>Brand</InputLabel>
                <Select
                  multiple
                  value={filters.brandNames}
                  onChange={handleMultiSelectChange("brandNames")}
                  input={<OutlinedInput notched label="Brand" />}
                  renderValue={(selected) => renderMultiValue(selected, "All Brands")}
                  displayEmpty
                  MenuProps={multiSelectMenuProps}
                >
                  {brands.map((b) => {
                    const isChecked = filters.brandNames.indexOf(b.name) > -1;
                    return (
                      <MenuItem key={b.id} value={b.name}>
                        <Checkbox checked={isChecked} size="small" sx={{ mr: 1, p: 0.5 }} />
                        <ListItemText
                          primary={b.name}
                          primaryTypographyProps={{ fontSize: 14, fontWeight: isChecked ? 600 : 400, noWrap: true }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel shrink>Sub-Category</InputLabel>
                <Select
                  multiple
                  value={filters.subCategoryNames}
                  onChange={handleMultiSelectChange("subCategoryNames")}
                  input={<OutlinedInput notched label="Sub-Category" />}
                  renderValue={(selected) => renderMultiValue(selected, "All Sub-Categories")}
                  displayEmpty
                  MenuProps={multiSelectMenuProps}
                >
                  {subCategoryNames.map((sc) => {
                    const isChecked = filters.subCategoryNames.indexOf(sc) > -1;
                    return (
                      <MenuItem key={sc} value={sc}>
                        <Checkbox checked={isChecked} size="small" sx={{ mr: 1, p: 0.5 }} />
                        <ListItemText
                          primary={sc}
                          primaryTypographyProps={{ fontSize: 14, fontWeight: isChecked ? 600 : 400, noWrap: true }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel shrink>Occasion</InputLabel>
                <Select
                  multiple
                  value={filters.occasionIds}
                  onChange={handleMultiSelectChange("occasionIds")}
                  input={<OutlinedInput notched label="Occasion" />}
                  renderValue={(selected) => renderMultiValue(selected.map(collectionNameById), "All Occasions")}
                  displayEmpty
                  MenuProps={multiSelectMenuProps}
                >
                  {collections.filter((c) => c.group === "OCCASION").map((c) => {
                    const isChecked = filters.occasionIds.indexOf(c.id) > -1;
                    return (
                      <MenuItem key={c.id} value={c.id}>
                        <Checkbox checked={isChecked} size="small" sx={{ mr: 1, p: 0.5 }} />
                        <ListItemText
                          primary={c.name}
                          primaryTypographyProps={{ fontSize: 14, fontWeight: isChecked ? 600 : 400, noWrap: true }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel shrink>Celebration</InputLabel>
                <Select
                  multiple
                  value={filters.celebrationIds}
                  onChange={handleMultiSelectChange("celebrationIds")}
                  input={<OutlinedInput notched label="Celebration" />}
                  renderValue={(selected) => renderMultiValue(selected.map(collectionNameById), "All Celebrations")}
                  displayEmpty
                  MenuProps={multiSelectMenuProps}
                >
                  {collections.filter((c) => c.group === "CELEBRATION").map((c) => {
                    const isChecked = filters.celebrationIds.indexOf(c.id) > -1;
                    return (
                      <MenuItem key={c.id} value={c.id}>
                        <Checkbox checked={isChecked} size="small" sx={{ mr: 1, p: 0.5 }} />
                        <ListItemText
                          primary={c.name}
                          primaryTypographyProps={{ fontSize: 14, fontWeight: isChecked ? 600 : 400, noWrap: true }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" type="number" label="Min Price" sx={fieldSx}
                value={filters.minPrice}
                onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" type="number" label="Max Price" sx={fieldSx}
                value={filters.maxPrice}
                onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>SCM User</InputLabel>

                <Select
                  value={filters.uploadedBy}
                  label="SCM User"
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      uploadedBy: e.target.value,
                    })
                  }
                >
                  <MenuItem value="">All SCM Users</MenuItem>

                  {SCM_ADMIN_EMAILS.map((email) => (
                    <MenuItem key={email} value={email}>
                      {email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Button
                fullWidth variant="contained" disableElevation startIcon={<SearchIcon />}
                onClick={() => performSearch(1)}
                disabled={loading}
                sx={{
                  borderRadius: `${catalogTokens.radius.md}px`,
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1,
                  bgcolor: catalogTokens.color.primary,
                  "&:hover": { bgcolor: catalogTokens.color.primaryDark },
                }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Box>

        {activeChips.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
            {activeChips.map((chip) => (
              <Chip
                key={`${chip.key}-${chip.value ?? chip.label}`}
                label={chip.label}
                size="small"
                onDelete={() => clearFilter(chip.key, chip.value)}
                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{ bgcolor: catalogTokens.color.primarySoft, color: catalogTokens.color.primary, fontWeight: 600 }}
              />
            ))}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: `${catalogTokens.radius.md}px` }} onClose={() => setError(null)}>{error}</Alert>}

        {!loading && results.length > 0 && (
          <Typography variant="body2" sx={{ color: catalogTokens.color.textSecondary, mb: 1.5 }}>
            {totalResults} {totalResults === 1 ? "catalog" : "catalogs"} found · grouped into {vendorGroups.length} {vendorGroups.length === 1 ? "card" : "cards"} on this page
          </Typography>
        )}

        {loading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Skeleton variant="rounded" height={188} sx={{ borderRadius: `${catalogTokens.radius.lg}px` }} />
              </Grid>
            ))}
          </Grid>
        ) : results.length === 0 ? (
          <Box
            sx={{
              py: 8,
              textAlign: "center",
              borderRadius: `${catalogTokens.radius.lg}px`,
              border: `1px dashed ${catalogTokens.color.border}`,
            }}
          >
            <InventoryIcon sx={{ fontSize: 40, color: catalogTokens.color.textSecondary, mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: catalogTokens.color.textPrimary }}>
              No catalogs match this search
            </Typography>
            <Typography variant="body2" sx={{ color: catalogTokens.color.textSecondary }}>
              Try widening the price range or clearing a filter.
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {vendorGroups.map((group) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={group.vendorId}>
                  <VendorCatalogCard
                    group={group}
                    onOpenVendorCatalogs={(g) => setVendorDialogTarget({ id: g.vendorId, name: g.vendorName })}
                  />
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  count={totalPages} page={page}
                  onChange={(_, value) => performSearch(value)}
                  color="primary" shape="rounded"
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <VendorCatalogVendorDialog
        vendor={vendorDialogTarget}
        open={!!vendorDialogTarget}
        onClose={() => setVendorDialogTarget(null)}
        canManage={canManage}
        onChanged={() => performSearch(page, { silent: true })}
        filters={filters}
        selectedMonth={selectedMonth}
      />
    </Box>
  );
};

export default VendorCatalogSearch;
